import { useState } from 'react';
import { Mail, MapPin, Instagram, Twitter, Linkedin, Send, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// TikTok icon component (not in lucide-react)
const TikTok = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const socialLinks = [
  { name: 'Instagram', icon: Instagram, url: 'https://www.instagram.com/ourvillage' },
  { name: 'Twitter', icon: Twitter, url: 'https://www.x.com/OVYourcommunity' },
  { name: 'LinkedIn', icon: Linkedin, url: 'https://www.linkedin.com/in/our-village-your-community/' },
  { name: 'TikTok', icon: TikTok, url: 'https://www.tiktok.com/@ourvillageyc' },
];

const enquiryTypes = [
  { value: 'partnership', label: 'Partnership' },
  { value: 'volunteer', label: 'Volunteer' },
  { value: 'media', label: 'Media / Press' },
  { value: 'insight-report', label: 'Insight Report Enquiry' },
  { value: 'general', label: 'General Enquiry' },
];

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    organisation: '',
    email: '',
    enquiry_type: '',
    message: '',
    availability: '',
    skills: '',
    partnership_type: '',
    budget_range: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ 
      ...prev, 
      enquiry_type: value,
      // Reset conditional fields when enquiry type changes
      availability: '',
      skills: '',
      partnership_type: '',
      budget_range: '',
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSuccess(true);
      setFormData({
        name: '',
        organisation: '',
        email: '',
        enquiry_type: '',
        message: '',
        availability: '',
        skills: '',
        partnership_type: '',
        budget_range: '',
      });
    } catch (err) {
      setError('Something went wrong. Please try again or email us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main data-testid="contact-page" className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 bg-[#FAF9F6]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <span className="text-sm font-medium tracking-wide uppercase text-[#E3B505] mb-4 block">Contact Us</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] text-[#2D2A26] mb-6 font-['Manrope']">
              Let's Start a Conversation
            </h1>
            <p className="text-lg md:text-xl text-[#5C5852] leading-relaxed">
              Whether you're interested in partnering with us, volunteering, or just want to learn 
              more about OVYC, we'd love to hear from you.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-3 gap-12 lg:gap-16">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <h2 className="text-2xl font-semibold text-[#2D2A26] mb-6 font-['Manrope']">
                Get In Touch
              </h2>
              
              <div className="space-y-6 mb-10">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#E3B505]/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-[#E3B505]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#5C5852] mb-1">Email us at</p>
                    <a 
                      href="mailto:hello@ovyc.co.uk"
                      data-testid="contact-email"
                      className="text-[#2D2A26] font-medium hover:text-[#E3B505] transition-colors"
                    >
                      hello@ovyc.co.uk
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#264653]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-[#264653]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#5C5852] mb-1">Based in</p>
                    <p className="text-[#2D2A26] font-medium">London, United Kingdom</p>
                  </div>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-[#5C5852] mb-4">Follow us</p>
                <div className="flex items-center gap-3">
                  {socialLinks.map((social) => (
                    <a
                      key={social.name}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`contact-social-${social.name.toLowerCase()}`}
                      className="w-11 h-11 rounded-full bg-[#FAF9F6] flex items-center justify-center hover:bg-[#E3B505] hover:text-[#2D2A26] text-[#5C5852] transition-all duration-300"
                      aria-label={social.name}
                    >
                      <social.icon className="w-5 h-5" />
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              {isSuccess ? (
                <div 
                  data-testid="success-message"
                  className="bg-[#E3B505]/10 rounded-3xl p-8 md:p-12 text-center"
                >
                  <div className="w-16 h-16 rounded-full bg-[#E3B505] flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-8 h-8 text-[#2D2A26]" />
                  </div>
                  <h3 className="text-2xl font-semibold text-[#2D2A26] mb-4 font-['Manrope']">
                    Message Sent Successfully!
                  </h3>
                  <p className="text-[#5C5852] text-lg mb-6">
                    Thank you for reaching out. We'll get back to you as soon as possible.
                  </p>
                  <Button 
                    onClick={() => setIsSuccess(false)}
                    data-testid="send-another-btn"
                    className="bg-[#264653] text-white hover:bg-[#264653]/90 rounded-full h-12 px-8"
                  >
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <form 
                  onSubmit={handleSubmit}
                  data-testid="contact-form"
                  className="bg-[#FAF9F6] rounded-3xl p-8 md:p-10"
                >
                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    {/* Name */}
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-[#2D2A26] font-medium">
                        Your Name *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        data-testid="input-name"
                        placeholder="Enter your name"
                        className="h-12 rounded-xl border-[#E6E2D8] focus:border-[#E3B505] focus:ring-[#E3B505]"
                      />
                    </div>

                    {/* Organisation */}
                    <div className="space-y-2">
                      <Label htmlFor="organisation" className="text-[#2D2A26] font-medium">
                        Organisation
                      </Label>
                      <Input
                        id="organisation"
                        name="organisation"
                        type="text"
                        value={formData.organisation}
                        onChange={handleChange}
                        data-testid="input-organisation"
                        placeholder="Your organisation (optional)"
                        className="h-12 rounded-xl border-[#E6E2D8] focus:border-[#E3B505] focus:ring-[#E3B505]"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    {/* Email */}
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-[#2D2A26] font-medium">
                        Email Address *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        data-testid="input-email"
                        placeholder="your@email.com"
                        className="h-12 rounded-xl border-[#E6E2D8] focus:border-[#E3B505] focus:ring-[#E3B505]"
                      />
                    </div>

                    {/* Enquiry Type */}
                    <div className="space-y-2">
                      <Label className="text-[#2D2A26] font-medium">
                        Enquiry Type *
                      </Label>
                      <Select 
                        value={formData.enquiry_type} 
                        onValueChange={handleSelectChange}
                        required
                      >
                        <SelectTrigger 
                          data-testid="select-enquiry-type"
                          className="h-12 rounded-xl border-[#E6E2D8] focus:border-[#E3B505] focus:ring-[#E3B505]"
                        >
                          <SelectValue placeholder="Select enquiry type" />
                        </SelectTrigger>
                        <SelectContent>
                          {enquiryTypes.map((type) => (
                            <SelectItem 
                              key={type.value} 
                              value={type.value}
                              data-testid={`enquiry-option-${type.value}`}
                            >
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Conditional Fields for Partnership */}
                  {formData.enquiry_type === 'partnership' && (
                    <div className="grid md:grid-cols-2 gap-6 mb-6 animate-fade-in">
                      <div className="space-y-2">
                        <Label htmlFor="partnership_type" className="text-[#2D2A26] font-medium">
                          Partnership Type
                        </Label>
                        <Select 
                          value={formData.partnership_type} 
                          onValueChange={(value) => setFormData(prev => ({ ...prev, partnership_type: value }))}
                        >
                          <SelectTrigger 
                            data-testid="select-partnership-type"
                            className="h-12 rounded-xl border-[#E6E2D8] focus:border-[#E3B505] focus:ring-[#E3B505]"
                          >
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="event-sponsorship">Event Sponsorship</SelectItem>
                            <SelectItem value="programme-partnership">Programme Partnership</SelectItem>
                            <SelectItem value="in-kind-support">In-Kind Support</SelectItem>
                            <SelectItem value="brand-activation">Brand Activation</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="budget_range" className="text-[#2D2A26] font-medium">
                          Budget Range
                        </Label>
                        <Select 
                          value={formData.budget_range} 
                          onValueChange={(value) => setFormData(prev => ({ ...prev, budget_range: value }))}
                        >
                          <SelectTrigger 
                            data-testid="select-budget-range"
                            className="h-12 rounded-xl border-[#E6E2D8] focus:border-[#E3B505] focus:ring-[#E3B505]"
                          >
                            <SelectValue placeholder="Select range" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="under-1k">Under £1,000</SelectItem>
                            <SelectItem value="1k-5k">£1,000 - £5,000</SelectItem>
                            <SelectItem value="5k-10k">£5,000 - £10,000</SelectItem>
                            <SelectItem value="10k-plus">£10,000+</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}

                  {/* Conditional Fields for Volunteer */}
                  {formData.enquiry_type === 'volunteer' && (
                    <div className="grid md:grid-cols-2 gap-6 mb-6 animate-fade-in">
                      <div className="space-y-2">
                        <Label htmlFor="availability" className="text-[#2D2A26] font-medium">
                          Availability
                        </Label>
                        <Select 
                          value={formData.availability} 
                          onValueChange={(value) => setFormData(prev => ({ ...prev, availability: value }))}
                        >
                          <SelectTrigger 
                            data-testid="select-availability"
                            className="h-12 rounded-xl border-[#E6E2D8] focus:border-[#E3B505] focus:ring-[#E3B505]"
                          >
                            <SelectValue placeholder="Select availability" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="weekdays">Weekdays</SelectItem>
                            <SelectItem value="weekends">Weekends</SelectItem>
                            <SelectItem value="evenings">Evenings</SelectItem>
                            <SelectItem value="flexible">Flexible</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="skills" className="text-[#2D2A26] font-medium">
                          Skills / Experience
                        </Label>
                        <Input
                          id="skills"
                          name="skills"
                          type="text"
                          value={formData.skills}
                          onChange={handleChange}
                          data-testid="input-skills"
                          placeholder="e.g., Event support, Photography"
                          className="h-12 rounded-xl border-[#E6E2D8] focus:border-[#E3B505] focus:ring-[#E3B505]"
                        />
                      </div>
                    </div>
                  )}

                  {/* Message */}
                  <div className="space-y-2 mb-6">
                    <Label htmlFor="message" className="text-[#2D2A26] font-medium">
                      Your Message *
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      data-testid="input-message"
                      placeholder="Tell us more about your enquiry..."
                      rows={5}
                      className="rounded-xl border-[#E6E2D8] focus:border-[#E3B505] focus:ring-[#E3B505] resize-none"
                    />
                  </div>

                  {error && (
                    <div 
                      data-testid="error-message"
                      className="mb-6 p-4 bg-red-50 text-red-600 rounded-xl text-sm"
                    >
                      {error}
                    </div>
                  )}

                  <Button 
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="submit-btn"
                    className="w-full md:w-auto bg-[#E3B505] text-[#2D2A26] hover:bg-[#E3B505]/90 rounded-full h-14 px-10 text-base font-medium shadow-md disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      'Sending...'
                    ) : (
                      <>
                        Send Message
                        <Send className="ml-2 w-5 h-5" />
                      </>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ContactPage;
